import { create } from 'zustand';
import type { TaskEvent, TaskRecord } from '@/api/tasks';

export interface TaskWsEnvelope {
  v: 1;
  seq: number;
  ts: number;
  sessionId: string;
  agentId: string;
  type: 'task.created' | 'task.updated' | 'task.terminal' | 'task.event';
  payload: TaskRecord | { runId: string; patch: Partial<TaskRecord> } | { runId: string; outcome?: string } | TaskEvent;
}

interface TaskEventsState {
  subscribers: Record<string, number>;
  byRun: Record<string, TaskEvent[]>;
  subscribe: (runId: string) => void;
  unsubscribe: (runId: string) => void;
  appendEvent: (runId: string, event: TaskEvent) => void;
}

export const useTaskEventsStore = create<TaskEventsState>((set) => ({
  subscribers: {},
  byRun: {},
  subscribe: (runId) =>
    set((s) => {
      const next = { ...s.subscribers };
      next[runId] = (next[runId] ?? 0) + 1;
      return { subscribers: next };
    }),
  unsubscribe: (runId) =>
    set((s) => {
      const next = { ...s.subscribers };
      const cur = (next[runId] ?? 0) - 1;
      if (cur <= 0) delete next[runId];
      else next[runId] = cur;
      return { subscribers: next };
    }),
  appendEvent: (runId, event) =>
    set((s) => {
      const list = [...(s.byRun[runId] ?? []), event];
      return { byRun: { ...s.byRun, [runId]: list } };
    }),
}));

/**
 * Demultiplex a task.* envelope into the store. Exposed on its own (rather
 * than as part of a long-lived WS hook) so unit tests can drive the store
 * without standing up a real websocket. The chatStream WS subscriber wires
 * this in Task 20.
 */
export function applyEnvelope(envelope: TaskWsEnvelope): void {
  const { type, payload } = envelope;
  if (!type.startsWith('task.')) return;
  if (type === 'task.event') {
    const evt = payload as TaskEvent;
    const runId = evt.pk.replace(/^TASK#/, '');
    useTaskEventsStore.getState().appendEvent(runId, evt);
  }
  // task.created / updated / terminal are handled by query-cache writes in
  // hooks/useTaskList.ts (Task 21). We only own raw event log here.
}

// Test seam.
export const applyEnvelopeForTesting = applyEnvelope;
